<script setup>
import { ref, reactive, computed, inject, watch } from "vue"
import { wiseAllowedCountries } from '@/data/wiseAllowedCountries.js'
import Checkbox from '@/components/common/Checkbox.vue'
import { useQuery, useMutation } from "@vue/apollo-composable"
import { createToaster } from "@meforma/vue-toaster"
import WISE_GET_CURRENCIES from "@/graphql/PaymentOption/WiseGetCurrencies.gql"
import UPDATE_CREATE_WISE_ACCOUNT from "@/graphql/PaymentOption/WiseUpdateCreateAccount.gql"
import UPDATE_CREATE_USER_WALLET_DATA from "@/graphql/PaymentOption/UpdateCreateUserWalletData.gql"
import WISE_ACCOUNT_DETAILS from "@/graphql/PaymentOption/WiseGetAccountDetails.gql"

const props = defineProps({
  user: {
    type: Object,
  },
  changeDetails: {
    type: Boolean,
    default: false,
  },
})

//close modal
const emit = defineEmits(["close"])
const close = () => {
  emit("close")
}
const refetchUserData = inject("refetchUserData")

// fields
const errors = ref(null)
const resetDetails = ref(false)
const wiseCurrency = ref(null)
const paymentType = ref(null)
const payOption = ref(null)
const wiseAccountDetails = ref({})
const filters = reactive({
  name: {},
  aDetails: {},
})
const quoteId = ref(null)
const wiseCurrencies = ref([])
const payOptions = ref([])
const walletId = ref(null)
const userId = computed(() => props?.user?.id ?? undefined)

// Computed property for payment type options based on userPaymentServiceOptions or userId
const paymentTypeOptions = computed(() => {
  if (userId.value == 28430) {
    return ['Bank Transfer (via Wise)']
  }
  const userPaymentServiceOptions = props.user?.userDetails?.userPaymentServiceOptions
  if (userPaymentServiceOptions !== null && userPaymentServiceOptions !== undefined) {
    try {
      return JSON.parse(userPaymentServiceOptions)
    } catch {
      // Invalid JSON, fall through to default options
    }
  }
})

const accountId = computed(() => {
  return props.changeDetails
    ? undefined
    : props?.user?.userDetails?.wiseAccountId ?? undefined
})

const toaster = createToaster({
  position: "bottom-right",
  duration: 4000,
})

//Wise Create Account
const { mutate: wiseCreateAccount, loading: isSubmitting } = useMutation(
  UPDATE_CREATE_WISE_ACCOUNT,
)

const { mutate: createBitcoinWallet, loading: isSubmittingWallet } =
  useMutation(UPDATE_CREATE_USER_WALLET_DATA)

const saveBitcoinWallet = e => {
  e.preventDefault()
  if (!validateFields()) {
    return
  }
  createBitcoinWallet({
    input: {
      walletId: walletId?.value,
    },
  })
    .then(data => {
      if (data?.data?.updateCreateUserWalletData?.errors) {
        errors.value = data?.data?.updateCreateUserWalletData?.errors
        return
      }
      if (!data?.data?.updateCreateUserWalletData?.errors) {
        if (refetchUserData) {
          refetchUserData()
        }
        emit("close")
        emit("updateWiseDetails")
        toaster.show("Wallet details successfully created", {
          type: "success",
        })
      }
    })
    .catch(error => {
      console.log("error", error)
      toaster.show("Something went wrong.", { type: "error" })
    })
}

const saveWiseDetails = e => {
  e.preventDefault()
  if (!validateFields()) {
    return
  }

  wiseCreateAccount({
    input: {
      wiseAccountId: accountId.value ?? undefined,
      fullName: filters.name.fullName,
      paymentType: payOption.value?.key,
      details: filters.aDetails,
      currencyCode: wiseCurrency.value?.code,
    },
  })
    .then(data => {
      if (data?.data?.wiseUpdateCreateRecipientAccount?.errors) {
        errors.value = data?.data?.wiseUpdateCreateRecipientAccount?.errors
        return
      }
      if (!data?.data?.wiseUpdateCreateRecipientAccount?.errors) {
        if (refetchUserData) {
          refetchUserData()
        }
        emit("close")
        emit("updateWiseDetails")
        toaster.show("Payment details successfully created", {
          type: "success",
        })
      }
    })
    .catch(error => {
      console.log("error", error)
      toaster.show("Something went wrong.", { type: "error" })
    })
}

const validationErrorFields = ref([])

const validateFields = () => {
  validationErrorFields.value = []
  var fieldsToCheck = {
    wiseCurrency: wiseCurrency.value,
    payOption: payOption.value,
    "filters.name.fullName": filters.name.fullName,
  }
  if (paymentType.value == "Crypto Wallet (BTC)") {
    fieldsToCheck = {
      walletId: walletId.value,
    }
  }

  Object.keys(fieldsToCheck).forEach(key => {
    const fieldValue = fieldsToCheck[key]
    if (!fieldValue) {
      validationErrorFields.value.push(key)
    }
  })

  if (paymentType.value == "Crypto Wallet (BTC)") {
    return validationErrorFields.value.length === 0
  }

  const accountRequirements = getAccountRequirements.value

  for (const requirement of accountRequirements) {
    if (requirement.required === false) {
      continue
    }
    const fieldKey = requirement.key
    const fieldValue = filters.aDetails[fieldKey]

    if (
      (requirement.fieldType === "input" || requirement.fieldType === "text") &&
      (!fieldValue ||
        (typeof fieldValue === "string" && fieldValue.trim() === ""))
    ) {
      validationErrorFields.value.push(requirement.name)
    } else if (
      requirement.fieldType === "select" &&
      (!fieldValue || !fieldValue.name)
    ) {
      validationErrorFields.value.push(requirement.name)
    }
  }

  return validationErrorFields.value.length === 0
}

// Wise Currencies
const { onResult: wiseGetCurrencies } = useQuery(WISE_GET_CURRENCIES)
wiseGetCurrencies(queryResult => {
  if (queryResult.data !== undefined) {
    wiseCurrencies.value = queryResult.data.wiseGetCurrencies
  }
})

// Wise Account Details Available
const wiseAccountDetailsInput = computed(() => ({
  wiseAccountId: accountId.value,
  currencyCode: wiseCurrency.value?.code,
  fullName: filters.name?.fullName ?? undefined,
  paymentType: payOption.value?.key ?? undefined,
  details: filters.aDetails ?? undefined,
  quoteId: quoteId.value ?? undefined,
}))

const shouldFetchAccountDetails = computed(() => !!props.user?.userDetails)
const { loading: isWiseLoading, onResult: getWiseAccountDetails } = useQuery(
  WISE_ACCOUNT_DETAILS,
  () => ({ input: wiseAccountDetailsInput.value }),
  {
    enabled: shouldFetchAccountDetails,
  },
)

getWiseAccountDetails(queryResult => {
  if (queryResult.data !== undefined) {
    wiseAccountDetails.value = queryResult.data
    payOptions.value = getPayOptions()
    createFilters(queryResult.data.wiseGetAccountDetails)
  }
})
watch(wiseCurrency, (newVal, oldVal) => {
  if (wiseCurrency.value?.code && newVal?.code !== oldVal?.code) {
    payOption.value = null
  }
})


function getPayOptions() {
  const options = []
  wiseAccountDetails?.value.wiseGetAccountDetails?.accountRequirementsData?.forEach(
    item => {
      options.push({
        title: item.title,
        key: item.key,
      })
    },
  )
  return options
}

const getAccountRequirements = computed(() => {
  if (
    wiseAccountDetails?.value.wiseGetAccountDetails?.accountRequirementsData
  ) {
    const filtered =
      wiseAccountDetails.value.wiseGetAccountDetails.accountRequirementsData
        .filter(element => element.key === payOption.value.key)[0]

    let requirements = (filtered?.requirementsData ?? []).filter(req => req.key !== 'forceWire')

    const hasRecipientCountry = requirements.some(
      req => req.key === 'address.country'
    )

    if (!hasRecipientCountry) {
      requirements = [
        ...requirements,
        {
          __typename: 'AccountRequirementsData',
          type: 'aba',
          title: 'ACH',
          name: 'Recipient country',
          fieldType: 'select',
          minLength: null,
          maxLength: null,
          key: 'address.country',
          valuesAllowed: wiseAllowedCountries,
        },
      ]
    }

    return requirements
  }

  return []
})

function createFilters(data) {
  for (const key in data.recipientAccount) {
    if (key === "__typename" || key === "displayFields") continue

    const value = data.recipientAccount[key]

    if (key === "type") {
      payOption.value = value
      continue
    }

    if (key === "currency" && !resetDetails.value) {
      wiseCurrency.value = value
      continue
    }

    if (typeof value === "object" && value !== null) {
      const innerObject = {}

      for (const innerKey in value) {
        if (innerKey === "__typename") continue

        // Adjust address fields as per your requirement
        if (key === "address") {
          innerObject[`address.${innerKey}`] = value[innerKey]
          if (innerKey === "state" || innerKey === "country") {
            innerObject[`address.${innerKey}`] = {
              key: value[innerKey],
              name: value[innerKey],
            }
          }
        } else {
          innerObject[innerKey] = value[innerKey]
        }
      }

      // Assign the values to `filters` as required
      if (key === "details") {
        filters.aDetails = innerObject
      } else {
        filters[key] = innerObject
      }
    } else {
      filters[key] = value
    }
  }
  if (filters.address) {
    filters.aDetails = { ...filters.address, ...filters.aDetails }
  }

  if (resetDetails.value) {
    payOption.value = null
    filters.aDetails = {}
    resetDetails.value = false
  }
}

const labelOverrides = {
  "Fedwire routing number": "Routing number",
  "Address Country": "Recipient Country",
  "Address Street Address": "Recipient Address",
  "Address Post Code": "Recipient Post Code",
  "Address City": "Recipient City",
  "Address State": "Recipient Region",
}

const formatLabel = name => labelOverrides[name] ?? name
</script>

<template>
  <div class="panel">
    <div class="panel-header flex justify-between">
      <h2 class="panel-title">Add Payment Details</h2>
      <button @click="close">
        <v-icon name="io-close" />
      </button>
    </div>
    <div class="panel-body">
      <form>
        <Widget>
          <template v-slot:header>{{
            changeDetails ? "New Payment Information" : "Create New Account"
          }}</template>

          <template v-slot:body>
            <div class="grid grid-cols-1 gap-4">
              <MultiSelect
                v-model="paymentType"
                inputLabel="Select Payment Type"
                placeholder="Select Payment Type"
                :options="paymentTypeOptions"
                :multiple="false"
                :required="true"
              />
              <div v-if="paymentType && paymentType == 'Crypto Wallet (BTC)'">
                <XInput
                  v-model="walletId"
                  label="Wallet Id"
                  :required="
                    paymentType == 'Crypto Wallet (BTC)' ? true : false
                  "
                  :error="validationErrorFields.includes('walletId')"
                ></XInput>
              </div>
              <div
                v-if="paymentType && paymentType == 'Bank Transfer (via Wise)'"
                class="grid grid-cols-1 gap-4"
              >
                <MultiSelect
                  v-model="wiseCurrency"
                  inputLabel="Select Currency"
                  placeholder="Select Currency"
                  :options="wiseCurrencies || []"
                  :multiple="false"
                  trackBy="code"
                  label="code"
                  selectedProperty="code"
                  :required="
                    paymentType != 'Crypto Wallet (BTC)' ? true : false
                  "
                  :error="validationErrorFields.includes('wiseCurrency')"
                />
                <MultiSelect
                  v-if="wiseCurrency"
                  v-model="payOption"
                  inputLabel="Payment Method"
                  placeholder="Payment Method"
                  :options="payOptions || []"
                  :multiple="false"
                  trackBy="title"
                  label="title"
                  selectedProperty="key"
                  :required="
                    paymentType != 'Crypto Wallet (BTC)' ? true : false
                  "
                  :error="validationErrorFields.includes('payOption')"
                />
              </div>

              <span v-if="paymentType">
                <div
                  v-if="payOption && paymentType != 'Crypto Wallet (BTC)'"
                  class="grid grid-cols-1 gap-4"
                >
                  <XInput
                    v-model="filters.name.fullName"
                    label="Account Holder"
                    :required="
                      paymentType != 'Crypto Wallet (BTC)' ? true : false
                    "
                    :error="
                      validationErrorFields.includes('filters.name.fullName')
                    "
                  ></XInput>
                  <XInput
                    v-model="filters.aDetails['email']"
                    label="Payment Notifications Email"
                    :required="
                      paymentType != 'Crypto Wallet (BTC)' ? true : false
                    "
                  ></XInput>
                  <div
                    v-for="(value, key) in getAccountRequirements"
                    :key="key"
                  >
                    <div
                      v-if="
                        value.fieldType == 'input' || value.fieldType == 'text'
                      "
                    >
                      <XInput
                        v-model="filters.aDetails[value?.key]"
                        :label="formatLabel(value?.name)"
                        :required="
                          paymentType != 'Crypto Wallet (BTC)' ? true : false
                        "
                        :error="validationErrorFields.includes(value?.name)"
                      ></XInput>
                    </div>
                    <div v-if="value.fieldType == 'select'">
                      <MultiSelect
                        v-model="filters.aDetails[value?.key]"
                        :inputLabel="formatLabel(value?.name)"
                        :placeholder="formatLabel(value?.name)"
                        :options="value?.valuesAllowed || []"
                        :multiple="false"
                        trackBy="name"
                        label="name"
                        selectedProperty="name"
                        :required="
                          paymentType != 'Crypto Wallet (BTC)' ? true : false
                        "
                        :error="validationErrorFields.includes(value?.name)"
                      />
                    </div>
                    <div v-if="value.fieldType == 'boolean'">
                      <Checkbox
                        v-model="filters.aDetails[value?.key]"
                        :label="formatLabel(value?.name)"
                      />
                    </div>
                  </div>
                </div>
              </span>
            </div>
          </template>

          <template v-slot:footer>
            <div class="flex flex-col">
              <div class="flex justify-end gap-6">
                <XButton color="ghost" @click="emit('close')">Cancel</XButton>
                <XButton
                  v-if="paymentType == 'Crypto Wallet (BTC)'"
                  color="accent"
                  @click="saveBitcoinWallet"
                  type="'submit'"
                  :isLoading="isSubmittingWallet"
                  >{{
                    changeDetails
                      ? "Submit New Payment Information"
                      : "Create New Account"
                  }}</XButton>
                <XButton
                  v-else
                  color="accent"
                  @click="saveWiseDetails"
                  type="'submit'"
                  :isLoading="isSubmitting"
                  >{{
                    changeDetails
                      ? "Submit New Payment Information"
                      : "Create New Account"
                  }}</XButton>
              </div>
              <div v-if="errors" class="flex-grow mt-2">
                <Alert
                  v-for="(error, index) in errors"
                  :key="index"
                  isShowing="true"
                  :alertMessage="error"
                  type="error"
                  class="mb-2"
                />
              </div>
            </div>
          </template>
        </Widget>
      </form>
    </div>
  </div>
</template>

<style scoped>
.vfm {
  @apply flex justify-center items-center;
}
.vfm__content {
  @apply flex flex-col w-6/12 max-w-screen-2xl mx-4 px-6 py-5 bg-base-100 border border-neutral-content rounded-md shadow-lg;
}
</style>
